import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Search, RefreshCw,
  FileText, Eye, CreditCard, BookOpen, Loader2, ClipboardList,
  CalendarClock, X, AlertTriangle, FileSpreadsheet, Printer, UserPlus,
  CheckCircle2,
} from "lucide-react";
import {
  exportExamApplicationsPdf,
  exportExamApplicationsSpreadsheet,
  type ExamExportRow,
} from "../../utils/exportExamApplications";
import {
  exportDietCoursesPdf,
  exportDietCoursesSpreadsheet,
  type DietCoursesReport,
} from "../../utils/exportDietCourses";
import {
  useAdminGetDietApplicationsQuery,
  useAdminGetDietQuery,
  useAdminGetApplicationDeferTargetsQuery,
  useAdminDeferApplicationMutation,
  useLazyAdminGetDietApplicationsExportQuery,
  useAdminGetDeferredCandidatesQuery,
  useAdminAddDeferredApplicationMutation,
  useAdminGetDietsByExamQuery,
  useAdminReviewApplicationMutation,
  useLazyAdminGetDietCoursesReportQuery,
  type DeferTargetCall,
} from "../../api/examsApi";
import AccessGate from "../../../../components/AccessGate";
import { PERMS } from "../../../../utils/perms";

/** Candidates per page. */
const PER_PAGE = 15;

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "pending_payment", label: "Awaiting payment" },
  { key: "paid", label: "Paid" },
  { key: "submitted", label: "Submitted" },
  { key: "under_review", label: "Under review" },
  // No "With committee" tab: the Membership Committee left the exam flow on
  // 2026-09-13 and the secretariat decides an application itself. Rows written
  // by that leg are moved to `under_review` by the retiring migration, so the
  // tab would be permanently empty — and an empty tab reads as "nobody is with
  // the committee", not as "there is no such thing".
  { key: "approved", label: "Approved" },
  { key: "graded", label: "Graded" },
  { key: "completed", label: "Completed" },
  { key: "rejected", label: "Rejected" },
] as const;

type StatusKey = (typeof STATUS_TABS)[number]["key"];

function statusColors(key: string) {
  switch (key) {
    case "completed":
      return { active: "bg-green-600 text-white border-green-600", inactive: "bg-green-50 text-green-800 hover:bg-green-100 border-green-200", pill: "bg-green-100 text-green-800 border-green-200" };
    case "graded":
      return { active: "bg-blue-600 text-white border-blue-600", inactive: "bg-blue-50 text-blue-800 hover:bg-blue-100 border-blue-200", pill: "bg-blue-100 text-blue-800 border-blue-200" };
    case "approved":
      return { active: "bg-violet-600 text-white border-violet-600", inactive: "bg-violet-50 text-violet-800 hover:bg-violet-100 border-violet-200", pill: "bg-violet-100 text-violet-800 border-violet-200" };
    case "paid":
      return { active: "bg-teal-600 text-white border-teal-600", inactive: "bg-teal-50 text-teal-800 hover:bg-teal-100 border-teal-200", pill: "bg-teal-100 text-teal-800 border-teal-200" };
    case "under_review":
      return { active: "bg-sky-700 text-white border-sky-700", inactive: "bg-sky-50 text-sky-900 hover:bg-sky-100 border-sky-300", pill: "bg-sky-100 text-sky-900 border-sky-300" };
    case "with_committee":
      return { active: "bg-indigo-600 text-white border-indigo-600", inactive: "bg-indigo-50 text-indigo-800 hover:bg-indigo-100 border-indigo-200", pill: "bg-indigo-100 text-indigo-800 border-indigo-200" };
    case "pending_payment":
      return { active: "bg-amber-600 text-white border-amber-600", inactive: "bg-amber-50 text-amber-800 hover:bg-amber-100 border-amber-200", pill: "bg-amber-100 text-amber-800 border-amber-200" };
    case "submitted":
      return { active: "bg-sky-600 text-white border-sky-600", inactive: "bg-sky-50 text-sky-800 hover:bg-sky-100 border-sky-200", pill: "bg-sky-100 text-sky-800 border-sky-200" };
    case "rejected":
      return { active: "bg-red-600 text-white border-red-600", inactive: "bg-red-50 text-red-800 hover:bg-red-100 border-red-200", pill: "bg-red-100 text-red-800 border-red-200" };
    case "draft":
      return { active: "bg-gray-600 text-white border-gray-600", inactive: "bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-300", pill: "bg-gray-100 text-gray-700 border-gray-300" };
    default:
      return { active: "bg-black text-white border-black", inactive: "bg-white text-gray-800 hover:bg-gray-50 border-gray-200", pill: "bg-gray-100 text-gray-800 border-gray-200" };
  }
}

const RESULT_COLORS: Record<string, string> = {
  passed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  recommended: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  not_recommended: "bg-rose-100 text-rose-800 border-rose-200",
  full_reset: "bg-rose-100 text-rose-800 border-rose-200",
  partial_pass: "bg-amber-100 text-amber-800 border-amber-200",
  referred: "bg-amber-100 text-amber-800 border-amber-200",
  absent: "bg-gray-100 text-gray-600 border-gray-200",
  pending: "bg-gray-100 text-gray-600 border-gray-200",
};

function StatusBadge({ status }: { status?: string }) {
  const s = String(status ?? "").toLowerCase();
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${statusColors(s).pill}`}>
      {s.replace(/_/g, " ") || "—"}
    </span>
  );
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// exam_applications.amount is stored in kobo.
const naira = (kobo?: number | null) =>
  kobo == null ? "—" : `₦${(Number(kobo) / 100).toLocaleString("en-NG")}`;

function fmtWindow(call: DeferTargetCall) {
  return `${fmtDate(call.opens_at)} – ${fmtDate(call.closes_at)}`;
}

/**
 * May this application be moved to another call?
 *
 * A completed application normally cannot: its verdict was a decision about
 * work the candidate did, and it would follow them to a sitting it was not made
 * at. ABSENT is the exception — it records that there was no work — so the
 * candidate may be carried over to sit properly. The server applies the same
 * rule; this only decides whether the button is offered.
 */
function canDefer(app: any) {
  return (
    app?.status !== "completed" || app?.result?.overall_status === "absent"
  );
}

/**
 * Every rung a candidate can sit on before anyone has decided their
 * application.
 *
 *   paid           the fee landed and nothing since — and where a DEFERRED
 *                  candidate sits, because deferring puts the application
 *                  back here. So carried-over candidates need no separate
 *                  test; `is_deferred` says where a row came from, not what
 *                  may be done to it.
 *   submitted      the ordinary resting place: paid, centre chosen, courses
 *                  registered, waiting on the chapter to endorse. Most of a
 *                  live diet is normally sitting here.
 *   under_review   endorsed, and in front of the secretariat.
 *   with_committee rows left behind by the retired committee leg.
 *
 * Left out deliberately: `draft` and `pending_payment` have not paid, and
 * `rejected` is a decision — the ladder says return it to the candidate
 * rather than quietly approve over the top of it. `approved`, `graded` and
 * `completed` are already past this.
 */
const APPROVABLE_STATUSES = ["paid", "submitted", "under_review", "with_committee"];

/**
 * May the secretariat approve this one straight from here?
 *
 * The server applies the same rule (DIRECTLY_APPROVABLE_STATUSES plus the
 * ladder's own); this only decides whether the button is offered.
 */
function canApproveNow(app: any) {
  return APPROVABLE_STATUSES.includes(String(app?.status ?? ""));
}

/**
 * Move one candidate's application to another CALL.
 *
 * Deferring is the secretariat's act, never the candidate's: they applied to
 * the call that was open, and only an admin may carry that application over to
 * a later one. Everything survives the move — the fee already paid, the
 * documents uploaded, the endorsement the chapter has given — which is the
 * whole reason to defer rather than make them apply again.
 *
 * Closed calls are offered as well as open ones, because a deferral is usually
 * made outside any window the candidate could have applied in themselves. Each
 * row says which it is, and a call the move would be refused for is shown with
 * its reason rather than hidden, so an admin is never left guessing why the one
 * they wanted is not there.
 */
function DeferModal({
  app,
  onClose,
  onDone,
}: {
  app: any;
  onClose: () => void;
  onDone: () => void;
}) {
  const [callId, setCallId] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading, isError } = useAdminGetApplicationDeferTargetsQuery(app.id);
  const [defer, { isLoading: deferring }] = useAdminDeferApplicationMutation();

  const calls = data?.data ?? [];
  const chosen = calls.find((c) => c.id === callId);
  const deferrable = data?.application?.deferrable !== false;

  // What the candidate is sitting, and therefore the only exam whose calls
  // are on offer. Read off the application rather than the first row, so it
  // still reads correctly when there are no other calls at all.
  const exam = data?.application?.exam;
  const examLabel = exam ? [exam.code, exam.title].filter(Boolean).join(" — ") : null;

  async function submit() {
    if (!callId) return;
    setErr(null);
    try {
      await defer({
        applicationId: app.id,
        target_call_id: callId,
        note: note.trim() || undefined,
      }).unwrap();
      onDone();
    } catch (e: any) {
      setErr(
        e?.data?.message ??
          "Could not defer this application. Please try again.",
      );
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 border border-amber-100">
              <CalendarClock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Defer application</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {app.user?.name ?? "Candidate"}
                {app.exam_no ? ` · ${app.exam_no}` : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div className="text-xs text-gray-500">
            Currently in{" "}
            <span className="font-medium text-gray-800">
              {data?.application?.call?.title ??
                data?.application?.diet?.title ??
                "this diet"}
            </span>
            . The fee already paid and the uploaded documents move with the
            application. It goes back to <strong>paid</strong>, and its centre
            and exam number are cancelled: the candidate chooses a centre for
            the new sitting, registers again and submits, and the number is
            issued then.
          </div>

          {/* Only this exam's calls are listed. Saying so matters because the
              rows below are titled by diet — "2026 Second Diet Application"
              reads the same under every exam — so a correct list is
              indistinguishable from a wrong one unless the exam is named. */}
          {examLabel && (
            <div className="text-xs text-gray-500">
              Showing calls for{" "}
              <span className="font-medium text-gray-800">{examLabel}</span>{" "}
              only. An application cannot be moved into another exam's call.
            </div>
          )}

          {!deferrable && (
            <div className="flex gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              This application is completed, so it can no longer be deferred.
              Only a candidate marked absent can be moved to a later call.
            </div>
          )}

          {isLoading ? (
            <div className="py-8 flex justify-center text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : isError ? (
            <div className="text-sm text-red-600">Failed to load calls.</div>
          ) : calls.length === 0 ? (
            <div className="p-3 rounded-lg border bg-gray-50 text-sm text-gray-600">
              There is no other call for {examLabel ?? "this exam"} to defer
              into. Create one on the exam calls page first — a call for a
              different exam cannot be used.
            </div>
          ) : (
            <div className="space-y-2">
              {calls.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition ${
                    c.blocked
                      ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                      : callId === c.id
                        ? "border-indigo-400 bg-indigo-50/60 cursor-pointer"
                        : "border-gray-200 hover:bg-gray-50 cursor-pointer"
                  }`}
                >
                  <input
                    type="radio"
                    name="defer-call"
                    className="mt-1"
                    disabled={c.blocked || !deferrable}
                    checked={callId === c.id}
                    onChange={() => setCallId(c.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">
                        {c.title || c.diet_title || "Untitled call"}
                      </span>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${
                          c.is_open
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-gray-100 text-gray-500 border-gray-200"
                        }`}
                      >
                        {c.is_open ? "Open" : "Closed"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {/* The exam leads the line: it is the one thing that
                          distinguishes this call from the identically-titled
                          call of another exam. */}
                      {c.exam?.code ? (
                        <span className="font-semibold text-gray-600">
                          {c.exam.code} ·{" "}
                        </span>
                      ) : null}
                      {c.diet_title ? `${c.diet_title} · ` : ""}
                      {fmtWindow(c)}
                    </div>
                    {c.blocked && (
                      <div className="text-xs text-amber-700 mt-1">
                        {c.blocked_reason}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Reason (kept on the deferral record)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. candidate requested deferral on medical grounds"
              className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>

          {/* Not open, but the admin chose it anyway — said plainly rather than
              refused, since deferring outside a window is the normal case. */}
          {chosen && !chosen.is_open && (
            <div className="flex gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              That call is closed. The candidate will be registered in it but
              will not be able to change their application until it reopens.
            </div>
          )}

          {err && <div className="text-sm text-red-600">{err}</div>}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50/60">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border bg-white text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!callId || deferring || !deferrable}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {deferring && <Loader2 className="w-4 h-4 animate-spin" />}
            {deferring ? "Deferring…" : "Defer application"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Every candidate in one diet: search, filter by status, and open each record. */
export default function AdminExamDietApplicationsPage() {
  const { examId = "", dietId = "" } = useParams();
  const location = useLocation();
  const passed = (location.state ?? {}) as { examTitle?: string; examCode?: string; dietTitle?: string };

  const [statusTab, setStatusTab] = useState<StatusKey>("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  /** The application being deferred, if any. */
  const [deferTarget, setDeferTarget] = useState<any | null>(null);
  /** Which row is being approved — so only that button spins, not all of them. */
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [approveErr, setApproveErr] = useState<string | null>(null);

  const { data: diet } = useAdminGetDietQuery(dietId, { skip: !dietId });
  const {
    data: rows = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useAdminGetDietApplicationsQuery({ dietId }, { skip: !dietId });

  const dietTitle = (diet as any)?.title ?? passed.dietTitle ?? "Diet";
  const examCode = (diet as any)?.exam?.code ?? passed.examCode ?? "";
  const examTitle = (diet as any)?.exam?.title ?? passed.examTitle ?? "";

  const [reviewApplication] = useAdminReviewApplicationMutation();

  /**
   * Approve one paid or carried-over candidate on the spot.
   *
   * Refetched rather than left to the cache: the review mutation invalidates
   * the application tags, not this diet's listing, which is how deferring from
   * this page already behaves.
   */
  async function approveOne(app: any) {
    const who = app.user?.name ?? "this candidate";
    const warning = app.exam_center_id
      ? ""
      : "\n\nThey have no centre yet, so no exam number will be issued until one "
        + "is set — and approving closes the centre screen to them, so an admin "
        + "has to set it from here.";

    if (!window.confirm(`Approve ${who}'s application?${warning}`)) return;

    setApprovingId(app.id);
    setApproveErr(null);

    try {
      await reviewApplication({ appId: app.id, action: "approve" }).unwrap();
      await refetch();
    } catch (e: any) {
      setApproveErr(
        `${who}: ${e?.data?.message || "could not be approved. Please try again."}`,
      );
    } finally {
      setApprovingId(null);
    }
  }

  const statusCounts = useMemo(() => {
    const m = new Map<string, number>();
    m.set("all", (rows as any[]).length);
    for (const r of rows as any[]) {
      const k = String(r.status ?? "").toLowerCase();
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return (rows as any[]).filter((r) => {
      if (statusTab !== "all" && String(r.status ?? "").toLowerCase() !== statusTab) return false;
      if (!nq) return true;
      const blob = [r.exam_no, r.user?.name, r.user?.email, r.user?.member?.membership_no, r.center?.name, r.reference]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(nq);
    });
  }, [rows, statusTab, q]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE),
    [filtered, safePage]
  );

  useEffect(() => { setPage(1); }, [q, statusTab]);

  /* ── Exports ────────────────────────────────────────────────────────────
     Two fixed listings — never the tab that happens to be open, and never the
     fifteen rows paged onto the screen:

       approved  the roll of who is sitting. Run over drafts and rejections it
                 would look official and say something untrue.
       paid      everyone whose fee has been settled, wherever their application
                 has since got to, with the reference, amount and date. Not the
                 `paid` TAB, which is only those still parked at that rung: a
                 list of who has paid that dropped everyone already approved
                 would be no use to the desk asking for it.

     The server fixes both scopes, so the two cannot drift.

     Fetched when the button is pressed rather than with the page: it carries
     each candidate's qualifications and posts held, which are hasMany relations
     nobody reading the status tabs wants loaded behind them. */
  const [fetchExport, { isFetching: exporting }] =
    useLazyAdminGetDietApplicationsExportQuery();
  const [exportErr, setExportErr] = useState<string | null>(null);
  /** Which of the four buttons is busy, so only that one shows the spinner. */
  const [busyExport, setBusyExport] = useState<string | null>(null);
  const [carryOpen, setCarryOpen] = useState(false);

  const runExport = async (scope: "approved" | "paid", as: "excel" | "pdf") => {
    setExportErr(null);
    setBusyExport(`${scope}:${as}`);
    try {
      const res: any = await fetchExport({ dietId, scope }).unwrap();
      const data = (res?.data ?? []) as ExamExportRow[];

      // An API that predates the paid scope answers the approved list and says
      // nothing about it. Printing that under a "Paid Applications" heading is
      // the one outcome worse than no export at all.
      if (scope === "paid" && res?.scope !== "paid") {
        setExportErr(
          "This server does not yet produce the paid list — the API update has not been deployed. The approved exports still work.",
        );
        return;
      }

      if (data.length === 0) {
        setExportErr(
          scope === "paid"
            ? "No application in this diet has been paid for yet, so there is nothing to export."
            : "No application in this diet has been approved yet, so there is nothing to export.",
        );
        return;
      }

      const meta = {
        exam: [examCode, examTitle].filter(Boolean).join(" — ") || "Exam",
        diet: dietTitle,
        // Headed off what the server actually produced, so a stale or ignored
        // parameter cannot print the wrong title over the right rows.
        scope: (res?.scope === "paid" ? "paid" : "approved") as "approved" | "paid",
      };

      if (as === "excel") exportExamApplicationsSpreadsheet(data, meta);
      else exportExamApplicationsPdf(data, meta);
    } catch (e: any) {
      setExportErr(e?.data?.message || "Could not build the export. Please try again.");
    } finally {
      setBusyExport(null);
    }
  };

  const [fetchCourses, { isFetching: fetchingCourses }] =
    useLazyAdminGetDietCoursesReportQuery();

  /**
   * Any of the six documents being built.
   *
   * `exporting` alone is the APPLICATIONS export's own fetch state, so it says
   * nothing while the courses report is loading — the two new buttons would
   * stay live and a second press would fire a duplicate.
   */
  const anyExportBusy = exporting || fetchingCourses || busyExport !== null;

  /**
   * The registered-courses report — subjects with their numbers, and every
   * candidate with the subjects they registered.
   */
  const runCoursesReport = async (as: "excel" | "pdf") => {
    setExportErr(null);
    setBusyExport(`courses:${as}`);

    try {
      const res: any = await fetchCourses({ dietId }).unwrap();

      // An API that predates this endpoint 404s, which arrives here as an
      // error — but one that answers something else entirely would otherwise
      // be printed as an empty report under a real-looking heading.
      if (!res || !Array.isArray(res.by_course) || !Array.isArray(res.candidates)) {
        setExportErr(
          "This server does not produce the registered-courses report yet — the API update has not been deployed.",
        );
        return;
      }

      const report = res as DietCoursesReport;

      if (report.totals.candidates === 0) {
        setExportErr(
          "There are no candidates in this diet yet, so there is nothing to report on.",
        );
        return;
      }

      if (as === "excel") exportDietCoursesSpreadsheet(report);
      else exportDietCoursesPdf(report);
    } catch (e: any) {
      setExportErr(
        e?.status === 404
          ? "This server does not produce the registered-courses report yet — the API update has not been deployed."
          : e?.data?.message || "Could not build the report. Please try again.",
      );
    } finally {
      setBusyExport(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <Link
        to={`/exams/admin/applications/exams/${examId}`}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3"
      >
        <ChevronLeft className="w-4 h-4" /> All diets
      </Link>

      <div className="bg-white border rounded-xl p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">{dietTitle}</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {examCode ? `${examCode} — ` : ""}{examTitle || "Exam applications"}
                {" · "}
                {(rows as any[]).length} candidate{(rows as any[]).length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Every one of them names its own scope, because that is what it
                produces whichever tab is open — a label reading just "Excel"
                next to a Rejected tab promises the rejected list. */}
            <button
              onClick={() => runExport("approved", "excel")}
              disabled={exporting}
              title="Download the numbered roll of approved candidates, with their qualifications and work experience"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50"
            >
              {busyExport === "approved:excel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Approved (Excel)
            </button>
            <button
              onClick={() => runExport("approved", "pdf")}
              disabled={exporting}
              title="Print the numbered roll of approved candidates, or save it as a PDF"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50"
            >
              {busyExport === "approved:pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              Approved (PDF)
            </button>
            <button
              onClick={() => runExport("paid", "excel")}
              disabled={exporting}
              title="Download every application whose fee has been settled, with its reference, amount and payment date"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-teal-200 text-teal-700 text-sm font-semibold hover:bg-teal-50 disabled:opacity-50"
            >
              {busyExport === "paid:excel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Paid (Excel)
            </button>
            <button
              onClick={() => runExport("paid", "pdf")}
              disabled={exporting}
              title="Print every application whose fee has been settled, or save it as a PDF"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-teal-200 text-teal-700 text-sm font-semibold hover:bg-teal-50 disabled:opacity-50"
            >
              {busyExport === "paid:pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              Paid (PDF)
            </button>
            {/* Registered courses. Named for the subject rather than the scope
                because it is not a third roll of candidates — it is what they
                signed up to sit, counted per subject and listed per person. */}
            <button
              onClick={() => runCoursesReport("excel")}
              disabled={anyExportBusy}
              title="Download every subject with the number registered, and every candidate with their subjects"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-violet-200 text-violet-700 text-sm font-semibold hover:bg-violet-50 disabled:opacity-50"
            >
              {busyExport === "courses:excel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Courses (Excel)
            </button>
            <button
              onClick={() => runCoursesReport("pdf")}
              disabled={anyExportBusy}
              title="Print the registered-courses report, or save it as a PDF"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-violet-200 text-violet-700 text-sm font-semibold hover:bg-violet-50 disabled:opacity-50"
            >
              {busyExport === "courses:pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              Courses (PDF)
            </button>
            <button
              onClick={() => setCarryOpen(true)}
              title="Add a candidate carried over from an earlier sitting this portal does not hold"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 text-indigo-700 text-sm font-semibold hover:bg-indigo-50"
            >
              <UserPlus className="w-4 h-4" />
              Carry in candidate
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* An export that quietly does nothing is the worst of the options —
            most often because nothing has been approved yet, which is a fact
            about the diet rather than a fault. */}
        {exportErr && (
          <div className="mt-4 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
            {exportErr}
          </div>
        )}

        {/* A refused approval. Up here with the other page-level notices rather
            than in the row, which is fifteen to a page and already narrow —
            and the server's own reason is what it says, since that is the only
            thing that explains a refusal. */}
        {approveErr && (
          <div className="mt-4 flex items-start justify-between gap-3 px-3 py-2 rounded-lg border border-rose-200 bg-rose-50 text-sm text-rose-900">
            <span>{approveErr}</span>
            <button
              onClick={() => setApproveErr(null)}
              className="shrink-0 text-rose-400 hover:text-rose-700"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Search */}
        <div className="mt-5 relative max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, exam no, membership no, center…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 mt-4">
          {STATUS_TABS.map((t) => {
            const active = statusTab === t.key;
            const c = statusColors(t.key);
            return (
              <button
                key={t.key}
                onClick={() => setStatusTab(t.key)}
                className={`px-3 py-2 text-sm border rounded-md transition ${active ? c.active : c.inactive}`}
              >
                {t.label}
                <span className={`ml-2 inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${c.pill}`}>
                  {statusCounts.get(t.key) || 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="mt-8 py-10 flex justify-center text-gray-300">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : isError ? (
          <div className="mt-6 text-sm text-red-600">Failed to load applications.</div>
        ) : filtered.length === 0 ? (
          <div className="mt-6 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
            {(rows as any[]).length === 0
              ? "No applications in this diet yet."
              : "No applications match your filters."}
          </div>
        ) : (
          <>
            <div className="mt-5 border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                    <th className="px-4 py-2 font-medium">Candidate</th>
                    <th className="px-4 py-2 font-medium">Exam No</th>
                    <th className="px-4 py-2 font-medium">Center</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Result</th>
                    <th className="px-4 py-2 font-medium">Fee</th>
                    <th className="px-4 py-2 font-medium">Submitted</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paged.map((r: any) => (
                    <tr key={r.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.user?.name ?? "—"}</div>
                        <div className="text-xs text-gray-400">
                          {r.user?.member?.membership_no ?? r.user?.email ?? ""}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {r.exam_no
                          ? <span className="font-mono text-xs font-semibold text-gray-800">{r.exam_no}</span>
                          : <span className="text-gray-300">—</span>}
                        {/* Read off the deferral record, not the number: the
                            move cancels the centre, so a carried-over candidate
                            has no number at all until they pick a new one. When
                            they do, it carries a trailing D. */}
                        {r.is_deferred && (
                          <div className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mt-0.5">
                            Deferred
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.center?.name ?? <span className="text-gray-300">—</span>}
                        {r.center?.state && <div className="text-xs text-gray-400">{r.center.state}</div>}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3">
                        {r.result?.overall_status ? (
                          <span
                            className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${
                              RESULT_COLORS[r.result.overall_status] ?? "bg-gray-100 text-gray-600 border-gray-200"
                            }`}
                          >
                            {String(r.result.overall_status).replace(/_/g, " ")}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {naira(r.amount)}
                        {r.paid_at && <div className="text-[10px] text-green-600 font-semibold">Paid</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {fmtDate(r.submitted_at ?? r.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* The full record — application particulars and the
                              form — rather than a summary in a box. */}
                          <Link
                            to={`/exams/admin/applications/${r.id}/form`}
                            title="View details"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/exams/admin/applications/${r.id}/card`}
                            title="Admission card"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                          >
                            <CreditCard className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/exams/admin/applications/${r.id}/courses`}
                            title="Registered courses"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                          >
                            <BookOpen className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/exams/admin/applications/${r.id}/form`}
                            title="Application form"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                          >
                            <ClipboardList className="w-4 h-4" />
                          </Link>
                          {/* Approve on the spot, for a candidate who has paid
                              or been carried over and is not on the roll yet.
                              Offered only where it applies — see
                              canApproveNow() — rather than shown disabled on
                              every other row, which would put a dead button on
                              most of the page. */}
                          {canApproveNow(r) && (
                            <AccessGate perm={PERMS.exam.applicationManage}>
                              <button
                                onClick={() => approveOne(r)}
                                disabled={approvingId === r.id}
                                title={
                                  r.exam_center_id
                                    ? "Approve this application"
                                    : "Approve — no centre set yet, so no exam number is issued"
                                }
                                className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40 disabled:cursor-wait"
                              >
                                {approvingId === r.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4" />
                                )}
                              </button>
                            </AccessGate>
                          )}
                          {/* Deferring is the secretariat's act — the candidate
                              cannot move their own application, which is why
                              this lives here and not in the member portal. */}
                          <AccessGate perm={PERMS.exam.applicationManage}>
                            <button
                              onClick={() => setDeferTarget(r)}
                              disabled={!canDefer(r)}
                              title={
                                !canDefer(r)
                                  ? "Completed applications cannot be deferred"
                                  : r.result?.overall_status === "absent"
                                    ? "Defer this absentee to another call"
                                    : "Defer to another call"
                              }
                              className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            >
                              <CalendarClock className="w-4 h-4" />
                            </button>
                          </AccessGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
              <p className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-700">{(safePage - 1) * PER_PAGE + 1}</span>–
                <span className="font-medium text-gray-700">
                  {Math.min(safePage * PER_PAGE, filtered.length)}
                </span>{" "}
                of <span className="font-medium text-gray-700">{filtered.length}</span>
              </p>

              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-1.5 rounded-lg border bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: pageCount }, (_, i) => i + 1)
                    .filter((n) => n === 1 || n === pageCount || Math.abs(n - safePage) <= 1)
                    .map((n, i, arr) => (
                      <React.Fragment key={n}>
                        {i > 0 && n - arr[i - 1] > 1 && <span className="px-1 text-xs text-gray-300">…</span>}
                        <button
                          onClick={() => setPage(n)}
                          className={`min-w-[2rem] px-2 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                            n === safePage
                              ? "bg-gray-900 text-white border-gray-900"
                              : "bg-white text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {n}
                        </button>
                      </React.Fragment>
                    ))}

                  <button
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={safePage === pageCount}
                    className="p-1.5 rounded-lg border bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {carryOpen && (
        <CarryInModal
          dietId={dietId}
          examId={examId}
          onClose={() => setCarryOpen(false)}
          onDone={() => {
            setCarryOpen(false);
            // A new candidate is now in this diet.
            refetch();
          }}
        />
      )}

      {deferTarget && (
        <DeferModal
          app={deferTarget}
          onClose={() => setDeferTarget(null)}
          onDone={() => {
            setDeferTarget(null);
            // The candidate has left this diet, so this list is now wrong.
            refetch();
          }}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Carry a candidate into this diet
 *
 * For a candidate coming from a sitting this portal never held — one that
 * predates it, or a registration that only ever existed on paper. There is no
 * application to defer, so one is created, marked as carried over rather than
 * newly registered.
 *
 * NO CENTRE IS ASKED FOR, deliberately. That is where an ordinary deferral
 * leaves a candidate too: the centre belongs to the new sitting and is theirs
 * to choose, and the exam number is built from it — so issuing one here would
 * mean inventing a centre to hang it on.
 * ------------------------------------------------------------------------- */
function CarryInModal({
  dietId,
  examId,
  onClose,
  onDone,
}: {
  dietId: string;
  examId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [term, setTerm] = useState("");
  const [picked, setPicked] = useState<{ user_id: number; name: string; membership_no: string | null } | null>(null);
  const [fromDietId, setFromDietId] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // Two characters minimum, matching the API — a shorter term is a scan of the
  // whole membership table and the server refuses it anyway.
  const { data: candidates = [], isFetching: searching } = useAdminGetDeferredCandidatesQuery(
    { dietId, q: term },
    { skip: term.trim().length < 2 },
  );

  // Where they might be coming from. Optional: the whole point of this screen
  // is candidates whose earlier sitting is not in here at all.
  const { data: diets = [] } = useAdminGetDietsByExamQuery(examId, { skip: !examId });

  const [add, { isLoading: saving }] = useAdminAddDeferredApplicationMutation();

  const submit = async () => {
    setErr(null);
    if (!picked) return setErr("Choose the candidate first.");
    if (note.trim().length < 5) return setErr("Say why this candidate is being carried over.");

    try {
      await add({
        dietId,
        body: {
          user_id: picked.user_id,
          from_diet_id: fromDietId || null,
          note: note.trim(),
        },
      }).unwrap();
      onDone();
    } catch (e: any) {
      setErr(e?.data?.message || "Could not add the candidate. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h3 className="font-bold text-gray-900">Carry in a candidate</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              For someone carried over from a sitting this portal does not hold.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Member</label>
            {picked ? (
              <div className="mt-1 flex items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                <div className="text-sm">
                  <p className="font-semibold text-indigo-900">{picked.name}</p>
                  <p className="text-xs text-indigo-700">{picked.membership_no || "No membership number"}</p>
                </div>
                <button
                  onClick={() => setPicked(null)}
                  className="text-xs font-semibold text-indigo-700 hover:underline"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="Name, membership number or email…"
                    className="w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
                {term.trim().length >= 2 && (
                  <div className="mt-2 border rounded-lg divide-y max-h-52 overflow-y-auto">
                    {searching && (
                      <div className="p-3 text-sm text-gray-400 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                      </div>
                    )}
                    {!searching && candidates.length === 0 && (
                      <div className="p-3 text-sm text-gray-500">
                        Nobody matches, or they are already in this diet.
                      </div>
                    )}
                    {candidates.map((c) => (
                      <button
                        key={c.user_id}
                        onClick={() => setPicked(c)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      >
                        <p className="text-sm font-medium text-gray-800">{c.name}</p>
                        <p className="text-xs text-gray-500">
                          {c.membership_no || "—"}{c.grade ? ` · ${c.grade}` : ""}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">
              Carried over from <span className="font-normal text-gray-400">(if it is in the portal)</span>
            </label>
            <select
              value={fromDietId}
              onChange={(e) => setFromDietId(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
            >
              <option value="">Not recorded here</option>
              {(diets as any[])
                .filter((d) => d.id !== dietId)
                .map((d) => (
                  <option key={d.id} value={d.id}>{d.title}</option>
                ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Why they are being carried over</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Deferred from the 2025 second diet; registration held on file."
              className="mt-1 w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
            />
            {/* The record is the only explanation there will ever be — nothing
                else on this application says where the candidate came from. */}
            <p className="mt-1 text-xs text-gray-400">
              Kept on the deferral record, and the only account of why this candidate is here.
            </p>
          </div>

          {err && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {err}
            </div>
          )}

          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            They are added as <strong>paid</strong>, with no centre — the same place an ordinary
            deferral leaves a candidate. Their exam number is issued once they choose a centre.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !picked}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Carry in
          </button>
        </div>
      </div>
    </div>
  );
}
